import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigationType, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import BreadCrumbs from '../BreadCrumbs';
import { Box, Button, Grid, Tooltip } from '@mui/material';
import SelectDataSettingsPanel from './SelectDataSettingsPanel';
import CompareAnalyteSettingsPanel from './CompareAnalyteSettingsPanel';
import CompareAnalyteDataTable from './CompareAnalyteDataTable';
import BoxplotModal from '../BoxPlotModal';
import DualScatterplotsSettingsPanel from './DualScatterplotsSettingsPanel';
import SearchVariantsSettingsPanel from './SearchVariantsSettingsPanel';
import SearchGenotypesSettingsPanel from './SearchGenotypesSettingsPanel';
import SearchVariantsResultsTable from './SearchVariantsResultsTable';
import SearchGenotypesResults from './SearchGenotypesResults';
import ScatterplotChart from '../ScatterPlotChart/index.js';
import Loading from '../Loading';
import APIUtils from '../../utils/APIUtils';
import { exportToCSV } from '../../utils/ExportToCSV';
import sanitizeForFilename from '../../utils/sanitizeForFilename';
import { FileDownload, CandlestickChart, Settings, InfoOutlined } from '@mui/icons-material';
import muiInputStyles from '../../uiComponents/MuiInput/MuiInputStyles';
import SessionConstants from '../../config/SessionConstants.js';
import PlotOptionsDialog from '../PlotOptionsDialog/index.js';

// Keys used to preserve the variant search state so the page can be restored
// after the user navigates to variant details and clicks the browser back button.
const VARIANT_SEARCH_STATE_KEY = 'exploreVariantSearchState';
const VARIANT_SEARCH_FORM_KEY = 'exploreVariantSearchForm';
// Preserves the results table pagination/sort across the round-trip to variant details.
const VARIANT_SEARCH_TABLE_KEY = 'exploreVariantSearchTable';
// One-time flag set when navigating to a variant details page from the results
// table. Its presence tells Explore to restore the search on the next mount.
const VARIANT_RESTORE_FLAG_KEY = 'exploreRestoreOnReturn';

// Keys used to preserve the genotype (patient) search flow so it can be restored
// after navigating to variant details and returning via the browser back button.
// Mirrors the variant search keys: flow/results state here, form inputs and the
// two tables' pagination/sort plus the selected columns/tab in the children.
const GENOTYPE_SEARCH_STATE_KEY = 'exploreGenotypeSearchState';
const GENOTYPE_SEARCH_FORM_KEY = 'exploreGenotypeSearchForm';
const GENOTYPE_VIEW_STATE_KEY = 'exploreGenotypeViewState';
const GENOTYPE_VARIANT_TABLE_KEY = 'exploreGenotypeVariantTable';
const GENOTYPE_PATIENTS_TABLE_KEY = 'exploreGenotypePatientsTable';

const getSavedVariantSearchState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(VARIANT_SEARCH_STATE_KEY)) || {};
  } catch {
    return {};
  }
};

const getSavedGenotypeSearchState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(GENOTYPE_SEARCH_STATE_KEY)) || {};
  } catch {
    return {};
  }
};

const Explore = () => {
  const classes = muiInputStyles();
  const navigationType = useNavigationType();
  const location = useLocation();
  // Restore only when returning to Explore right after visiting variant details:
  // the one-time flag must be set AND this must be a back/forward (POP) navigation.
  // Menu navigation (PUSH/REPLACE) starts fresh even if a stale flag remains.
  const shouldRestoreSearch =
    sessionStorage.getItem(VARIANT_RESTORE_FLAG_KEY) === '1' && navigationType === 'POP';
  const savedVariantSearch = shouldRestoreSearch ? getSavedVariantSearchState() : {};
  const restoreVariantSearch = savedVariantSearch.dataType === 'searchVariants';
  const savedGenotypeSearch = shouldRestoreSearch ? getSavedGenotypeSearchState() : {};
  const restoreGenotypeSearch = savedGenotypeSearch.dataType === 'searchGenotypes';
  const [loading, setLoading] = useState(false);
  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [dataType, setDataType] = useState(
    restoreVariantSearch ? 'searchVariants' : restoreGenotypeSearch ? 'searchGenotypes' : ''
  );
  const [compareAnalyteSettings, setCompareAnalyteSettings] = useState({ selectAnalyte: null, selectPrograms: [] });
  const [submitted, setSubmitted] = useState(restoreVariantSearch ? !!savedVariantSearch.submitted : false);
  const [tableData, setTableData] = useState([]);
  const [variantSearchResults, setVariantSearchResults] = useState(
    restoreVariantSearch && Array.isArray(savedVariantSearch.variantSearchResults)
      ? savedVariantSearch.variantSearchResults
      : []
  );
  const [boxPlotChartData, setBoxPlotChartData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [availableDataToView, setAvailableDataToView] = useState([]);
  const [analyteOptions, setAnalyteOptions] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [scatterplotOptionsData, setScatterplotOptionsData] = useState([]);
  const [programId, setProgramId] = useState(null);
  const [scatterplotData, setScatterplotData] = useState(null);
  const [showScatterplotChart, setShowScatterplotChart] = useState(false);
  const [scatterplotPlotOptionsOpen, setScatterplotPlotOptionsOpen] = useState(false);
  const [scatterplotDataScope, setScatterplotDataScope] = useState('my_program');
  const [scatterplotSettings, setScatterplotSettings] = useState(null);
  const [showScatterplotDensityCloud, setShowScatterplotDensityCloud] = useState(false);

  // Genotype search flow: variant results feed the "Variants to Search" picker,
  // and the user-selected variants drive the (upcoming) genotype search.
  const [genotypeVariantResults, setGenotypeVariantResults] = useState(
    restoreGenotypeSearch && Array.isArray(savedGenotypeSearch.genotypeVariantResults)
      ? savedGenotypeSearch.genotypeVariantResults
      : []
  );
  const [genotypeSubmitted, setGenotypeSubmitted] = useState(
    restoreGenotypeSearch ? !!savedGenotypeSearch.genotypeSubmitted : false
  );
  const [variantsToSearch, setVariantsToSearch] = useState(
    restoreGenotypeSearch && Array.isArray(savedGenotypeSearch.variantsToSearch)
      ? savedGenotypeSearch.variantsToSearch
      : []
  );
  // Genotype (patient) search results and the state that enables/activates the tab.
  const [genotypePatientResults, setGenotypePatientResults] = useState(
    restoreGenotypeSearch && Array.isArray(savedGenotypeSearch.genotypePatientResults)
      ? savedGenotypeSearch.genotypePatientResults
      : []
  );
  const [genotypeTabEnabled, setGenotypeTabEnabled] = useState(
    restoreGenotypeSearch ? !!savedGenotypeSearch.genotypeTabEnabled : false
  );
  const [genotypeSearchTrigger, setGenotypeSearchTrigger] = useState(0);
  // Incremented on each genotype-flow variant search so the results component
  // switches to the Variants tab to show the fresh results.
  const [genotypeVariantSearchTrigger, setGenotypeVariantSearchTrigger] = useState(0);

  const formatProgramId = (id) => (id === 'ALL_PROGRAMS' ? 'All Programs' : id);  
  const scatterplotRef = useRef();
  const ed3nConfig = __ED3N_CONFIG__;
  const didMountRef = useRef(false);
  const locationKeyRef = useRef(location.key);

  useEffect(() => {
    const programId = sessionStorage.getItem(SessionConstants.PROGRAM_ID);

    setProgramId(programId);

  }, []);

  useEffect(() => {
    // Consume the one-time restore flag so the search only persists for the
    // round-trip to and back from a variant details page. If this is not a
    // restore (refresh, menu navigation, etc.), clear any saved search state.
    sessionStorage.removeItem(VARIANT_RESTORE_FLAG_KEY);
    if (!shouldRestoreSearch) {
      sessionStorage.removeItem(VARIANT_SEARCH_STATE_KEY);
      sessionStorage.removeItem(VARIANT_SEARCH_FORM_KEY);
      sessionStorage.removeItem(VARIANT_SEARCH_TABLE_KEY);
      sessionStorage.removeItem(GENOTYPE_SEARCH_STATE_KEY);
      sessionStorage.removeItem(GENOTYPE_SEARCH_FORM_KEY);
      sessionStorage.removeItem(GENOTYPE_VIEW_STATE_KEY);
      sessionStorage.removeItem(GENOTYPE_VARIANT_TABLE_KEY);
      sessionStorage.removeItem(GENOTYPE_PATIENTS_TABLE_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Clicking the Explore menu link while already on this page is a same-route
    // navigation (PUSH/REPLACE) that changes the location key without remounting
    // the component. Treat that like a refresh: clear any saved/active search.
    if (locationKeyRef.current === location.key) return;
    locationKeyRef.current = location.key;
    if (navigationType !== 'POP') {
      sessionStorage.removeItem(VARIANT_RESTORE_FLAG_KEY);
      sessionStorage.removeItem(VARIANT_SEARCH_STATE_KEY);
      sessionStorage.removeItem(VARIANT_SEARCH_FORM_KEY);
      sessionStorage.removeItem(VARIANT_SEARCH_TABLE_KEY);
      sessionStorage.removeItem(GENOTYPE_SEARCH_STATE_KEY);
      sessionStorage.removeItem(GENOTYPE_SEARCH_FORM_KEY);
      sessionStorage.removeItem(GENOTYPE_VIEW_STATE_KEY);
      sessionStorage.removeItem(GENOTYPE_VARIANT_TABLE_KEY);
      sessionStorage.removeItem(GENOTYPE_PATIENTS_TABLE_KEY);
      setDataType('');
      setSubmitted(false);
      setVariantSearchResults([]);
      setCompareAnalyteSettings({ selectAnalyte: null, selectPrograms: [] });
      setTableData([]);
      setBoxPlotChartData([]);
      setShowScatterplotChart(false);
      setScatterplotData(null);
      setGenotypeVariantResults([]);
      setGenotypeSubmitted(false);
      setVariantsToSearch([]);
      setGenotypePatientResults([]);
      setGenotypeTabEnabled(false);
      setGenotypeSearchTrigger(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, navigationType]);

  useEffect(() => {
    setBreadCrumbs([{ name: 'Explore' }]);   
    
    // hard coded for available data types items
    ed3nConfig.disableBiochemical !== true ?
      setAvailableDataToView([
        {
          dataTypeId: 'compareAnalytePrograms',
          dataTypeName: 'Compare Analyte Data with Other Programs'
        },
        {
          dataTypeId: 'dualScatterplots',
          dataTypeName: 'Dual Scatterplots of Analyte Data'
        },
        {
          dataTypeId: 'searchVariants',
          dataTypeName: 'Search for Variants in Patients'
        },
        {
          dataTypeId: 'searchGenotypes',
          dataTypeName: 'Search for Genotypes in Patients'
        }
      ])
      :
      setAvailableDataToView([
        {
          dataTypeId: 'searchVariants',
          dataTypeName: 'Search for Variants in Patients'
        },
        {
          dataTypeId: 'searchGenotypes',
          dataTypeName: 'Search for Genotypes in Patients'
        }
      ]);
  }, []);

  useEffect(() => {
    // Skip the first run so restored variant search state is not cleared on mount
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    // Reset state when analyte or programs change
    setSubmitted(false);
    setTableData([]);
    setBoxPlotChartData([]);
  }, [compareAnalyteSettings.selectAnalyte, compareAnalyteSettings.selectPrograms]);

  useEffect(() => {
    // Persist the variant search state so it can be restored after navigating
    // to variant details and returning via the browser back button
    if (dataType === 'searchVariants') {
      sessionStorage.setItem(
        VARIANT_SEARCH_STATE_KEY,
        JSON.stringify({ dataType, submitted, variantSearchResults })
      );
    } else {
      sessionStorage.removeItem(VARIANT_SEARCH_STATE_KEY);
    }
  }, [dataType, submitted, variantSearchResults]);

  useEffect(() => {
    // Persist the genotype search flow (variant results, selected variants, and
    // patient results) so the whole flow can be restored after navigating to
    // variant details and returning via the browser back button. The form
    // inputs, selected columns/tab, and table pagination are persisted by the
    // respective child components.
    if (dataType === 'searchGenotypes') {
      sessionStorage.setItem(
        GENOTYPE_SEARCH_STATE_KEY,
        JSON.stringify({
          dataType,
          genotypeSubmitted,
          genotypeVariantResults,
          variantsToSearch,
          genotypePatientResults,
          genotypeTabEnabled,
        })
      );
    } else {
      sessionStorage.removeItem(GENOTYPE_SEARCH_STATE_KEY);
    }
  }, [
    dataType,
    genotypeSubmitted,
    genotypeVariantResults,
    variantsToSearch,
    genotypePatientResults,
    genotypeTabEnabled,
  ]);

  const handleDataTypeChange = async (newDataType) => {
    // Reset state when dataType changes
    setDataType(newDataType);
    setCompareAnalyteSettings({ selectAnalyte: null, selectPrograms: [] });
    setSubmitted(false);
    setTableData([]);
    setVariantSearchResults([]);
    setShowModal(false);
    setGenotypeVariantResults([]);
    setGenotypeSubmitted(false);
    setVariantsToSearch([]);
    setGenotypePatientResults([]);
    setGenotypeTabEnabled(false);
    setGenotypeSearchTrigger(0);

    if (newDataType === 'compareAnalytePrograms') {
      try {
        setLoading(true);
        // Fetch programs and analytes separately
        const [programsResponse, analytesResponse] = await Promise.all([
          APIUtils.getBiochemicalPrograms(),
          APIUtils.getBiochemicalAnalytes()
        ]);
        
        // Set program options from the programs array
        setProgramOptions(programsResponse.programs || []);
        
        // Set analyte options from the analytes array (store full objects)
        setAnalyteOptions(analytesResponse.analytes || []);
      } catch (error) {
        setLoading(false);
        console.error('Failed to fetch data for compareAnalytePrograms', error);
      }
      finally {
        setLoading(false);
      }
    }
    if (newDataType === 'dualScatterplots') {
      try {
        setLoading(true);
        const response = await APIUtils.getBiochemicalScatterplotAxisOptions();

        setScatterplotOptionsData(response);
        // console.log('Fetched initial settings data for dualScatterplots:', JSON.stringify(response));
      } catch (error) {
        setLoading(false);
        console.error('Failed to fetch data for dualScatterplots', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const roundNumericFields = (data) => {
    return data.map(item => {
      const roundedItem = {};
      for (const [key, value] of Object.entries(item)) {
        if (typeof value === 'number' && value !== undefined && value !== null) {
          roundedItem[key] = Math.round(value * 100) / 100; // number rounded to 2 decimals
        } else {
          roundedItem[key] = value;
        }
      }
      return roundedItem;
    });
  };

  const handleVariantSearch = async (body) => {
    try {
      setLoading(true);
      // Start a fresh search at page 1 with default rows-per-page
      sessionStorage.removeItem(VARIANT_SEARCH_TABLE_KEY);
      const response = await APIUtils.searchVariant(body, programId);
      setVariantSearchResults(Array.isArray(response) ? response : []);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to search variants', error);
      setVariantSearchResults([]);
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClearVariantSearch = () => {
    sessionStorage.removeItem(VARIANT_SEARCH_TABLE_KEY);
    setVariantSearchResults([]);
    setSubmitted(false);
  };

  const handleGenotypeVariantSearch = async (body) => {
    try {
      setLoading(true);
      // Start a fresh variant search at page 1 with default rows-per-page
      sessionStorage.removeItem(GENOTYPE_VARIANT_TABLE_KEY);
      const response = await APIUtils.searchVariant(body, programId);
      setGenotypeVariantResults(Array.isArray(response) ? response : []);
      setGenotypeSubmitted(true);
      setGenotypeVariantSearchTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Failed to search variants', error);
      setGenotypeVariantResults([]);
      setGenotypeSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  // Adds a variant from the results table to the "Variants to Search" list,
  // avoiding duplicates by variantName (the unique identifier). GRCh38 names can
  // be duplicated across different variants, so keying on them would let one
  // click add multiple rows; variantName is unique per variant.
  const handleAddVariantToSearch = (variant) => {
    setVariantsToSearch((prev) => {
      if (prev.some((v) => v.variantName === variant.variantName)) {
        return prev;
      }
      return [...prev, variant];
    });
  };

  // Removes a variant from the "Variants to Search" list by variantName.
  const handleRemoveVariantFromSearch = (variant) => {
    setVariantsToSearch((prev) => prev.filter((v) => v.variantName !== variant.variantName));
  };

  const handleClearGenotypeSearch = () => {
    // Clear persisted view/pagination so a subsequent restore does not bring
    // back stale columns or table state for a cleared search.
    sessionStorage.removeItem(GENOTYPE_VIEW_STATE_KEY);
    sessionStorage.removeItem(GENOTYPE_VARIANT_TABLE_KEY);
    sessionStorage.removeItem(GENOTYPE_PATIENTS_TABLE_KEY);
    setGenotypeVariantResults([]);
    setGenotypeSubmitted(false);
    setVariantsToSearch([]);
    setGenotypePatientResults([]);
    setGenotypeTabEnabled(false);
    setGenotypeSearchTrigger(0);
  };

  const handleSearchGenotypes = async () => {
    const variantObjectIds = variantsToSearch.map((v) => v.variantObjectId);
    try {
      setLoading(true);
      // Start a fresh patient search at page 1 with default rows-per-page
      sessionStorage.removeItem(GENOTYPE_PATIENTS_TABLE_KEY);
      const response = await APIUtils.searchGenotype(programId, variantObjectIds);
      setGenotypePatientResults(Array.isArray(response) ? response : []);
    } catch (error) {
      // A 400 from this endpoint means the selected variants have no matching
      // genotypes/patients — an expected "no results" case, not a real error.
      // Show the "No Search Results" message without logging noise; still log
      // any other (unexpected) failures.
      if (error?.response?.status === 400) {
        console.warn('No genotype data was returned for the selected variants.');
      } else {
        console.error('Failed to search genotypes', error);
      }
      setGenotypePatientResults([]);
    } finally {
      // Enable and activate the Genotypes tab regardless of the outcome.
      setGenotypeTabEnabled(true);
      setGenotypeSearchTrigger((prev) => prev + 1);
      setLoading(false);
    }
  };

  const handleSubmitAnalyteSettings = async () => {
    const { selectAnalyte, selectPrograms } = compareAnalyteSettings;

    if (!selectAnalyte || !Array.isArray(selectPrograms) || selectPrograms.length === 0) {
      setTableData([]);
      setBoxPlotChartData([]);
      return;
    }

    try {
      setLoading(true);
      
      // Extract analyte code from the selected analyte object
      const analyteCode = selectAnalyte.analyteCode;
      
      // Join selected programs as comma-separated string
      const programIds = selectPrograms.join(',');
      
      // Fetch statistics for all selected programs in a single API call
      const response = await APIUtils.getBiochemicalStatistics(programIds, analyteCode);
      
      // Store statistics for later use (boxplot modal)
      setStatistics(response.statistics || []);
      
      // Format program IDs for display
      const formatted = (response.statistics || []).map((entry) => ({
        ...entry,
        program_id: formatProgramId(entry.program_id),
        program_name: formatProgramId(entry.program_name || entry.program_id),
      }));

      // Validate each boxplot object
      const boxPlotData = formatted.filter(entry =>
        entry.min != null &&
        entry.max != null &&
        entry.q1 != null &&
        entry.q3 != null &&
        entry.median != null &&
        entry.mean != null &&
        entry.stddev != null &&
        entry.program_id != null
      );

      const roundedFiltered = roundNumericFields(formatted);
      const strippedBoxPlotData = boxPlotData.map(({ rendered_min, rendered_max, ...rest }) => rest);
      const roundedBoxPlotData = roundNumericFields(strippedBoxPlotData);

      setTableData(roundedFiltered);
      setBoxPlotChartData(roundedBoxPlotData);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to fetch biochemical statistics', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch statistics for Plot Options (only updates chart, not table)
  const handleFetchPlotOptionsData = async (programIds) => {
    const analyteCode = compareAnalyteSettings.selectAnalyte.analyteCode;
    
    const response = await APIUtils.getBiochemicalStatistics(programIds, analyteCode);
    
    // Format program IDs for display
    const formatted = (response.statistics || []).map((entry) => ({
      ...entry,
      program_id: formatProgramId(entry.program_id),
      program_name: formatProgramId(entry.program_name || entry.program_id),
    }));

    // Validate each boxplot object
    const boxPlotData = formatted.filter(entry =>
      entry.min != null &&
      entry.max != null &&
      entry.q1 != null &&
      entry.q3 != null &&
      entry.median != null &&
      entry.mean != null &&
      entry.stddev != null &&
      entry.program_id != null
    );

    return roundNumericFields(boxPlotData.map(({ rendered_min, rendered_max, ...rest }) => rest));
  };

  const handleSubmitScatterplotSettings = async (settings, overrideDataScope = null) => {
    const xAxis = settings.selectXAxisAnalyte;
    const yAxis = settings.selectYAxisAnalyte;
    const yAxisType = yAxis.includes(',') ? 'ratio' : 'analyte';

    // For ratio y-axis, convert comma-separated analyte codes to forward-slash-separated
    const yAxisValue = yAxisType === 'ratio' ? yAxis.split(',').join('/') : yAxis;

    // Determine which programId to use based on data scope. A normal Submit
    // (no override) always resets to my_program, so use that as the effective
    // scope here; Plot Options supplies its scope explicitly via overrideDataScope.
    const effectiveDataScope = overrideDataScope !== null ? overrideDataScope : 'my_program';
    const targetProgramId = effectiveDataScope === 'all_programs' ? 'ALL' : programId;

    const axisOptions = {
      xAxis,
      yAxis: yAxisValue,
      yAxisType,
      programId: targetProgramId
    };

    // Store settings for plot options re-fetch
    setScatterplotSettings(settings);

    try {
      setLoading(true);
      const response = await APIUtils.biochemicalScatterplot(axisOptions);
      // If called from Submit button (not Plot Options), reset to default scope and
      // view toggles now — after the fetch — so the previous chart (e.g. density cloud)
      // stays on screen until the new data is ready to render.
      if (overrideDataScope === null) {
        setScatterplotDataScope('my_program');
        setShowScatterplotDensityCloud(false);
      }
      setScatterplotData(response); // Save response data
      // console.log('Fetched scatterplot data:', JSON.stringify(response));
      setShowScatterplotChart(true); // Show chart
      setSubmitted(true);
    } catch (error) {
      console.error('Error fetching scatterplot data:', error);
      setShowScatterplotChart(false);
    } finally {
      setLoading(false);
    }
  };

  // Scatterplot plot options handler
  const handleScatterplotPlotOptionsSave = async (options) => {
    const { dataScope: newDataScope, showDensityCloud } = options;
    setScatterplotDataScope(newDataScope);
    setShowScatterplotDensityCloud(showDensityCloud);
    setScatterplotPlotOptionsOpen(false);
    // Re-fetch data with new scope (pass newDataScope directly to avoid stale state)
    if (scatterplotSettings) {
      await handleSubmitScatterplotSettings(scatterplotSettings, newDataScope);
    }
  };

  const csvHeaders = [
    { label: 'Program ID', key: 'program_id' },
    { label: 'Mean', key: 'mean' },
    { label: 'Median', key: 'median' },
    { label: 'Std. Dev.', key: 'stddev' },
    { label: 'Min', key: 'min' },
    { label: 'Q1', key: 'q1' },
    { label: 'Q3', key: 'q3' },
    { label: 'Max', key: 'max' },
    { label: 'N', key: 'n' }
  ];

  // Sort tableData by Program ID to match table display order
  const sortedTableData = [...tableData].sort((a, b) => {
    const aProgramId = a.program_id || '';
    const bProgramId = b.program_id || '';
    return aProgramId.localeCompare(bProgramId, undefined, { numeric: true, sensitivity: 'base' });
  });

  // Map only the defined headers to export to CSV
  const csvData = sortedTableData.map(row =>
    Object.fromEntries(
      csvHeaders.map(header => [header.key, row[header.key]])
    )
  );

  const canExportScatterplot = showScatterplotChart && (scatterplotData?.dataPoints?.length ?? 0) > 0;

  // Density cloud is computed client-side from the raw data points via
  // d3.contourDensity, so a couple of points is all that's required.
  const hasScatterplotDensityData = useMemo(() => {
    return (scatterplotData?.dataPoints?.length ?? 0) >= 2;
  }, [scatterplotData?.dataPoints]);

  const scatterplotDensityDisabledMessage = useMemo(() => {
    if (hasScatterplotDensityData) return '';
    return 'No population density data available';
  }, [hasScatterplotDensityData]);

  return (
    <>
      <Loading isLoading={loading}/>
      <Helmet>
        <title>ED3N - Explore Data</title>
      </Helmet>
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <h1 className="pageTitle">Explore Data</h1>
      <Box marginTop={3}>
        <SelectDataSettingsPanel
          classes={classes}
          dataType={dataType}
          availableDataToView={availableDataToView}
          onChange={handleDataTypeChange}
        />
        {dataType === 'compareAnalytePrograms' && (
          <CompareAnalyteSettingsPanel
            classes={classes}
            compareAnalyteSettings={compareAnalyteSettings}
            onChange={setCompareAnalyteSettings}
            onSubmit={handleSubmitAnalyteSettings}
            analyteOptions={analyteOptions}
            programOptions={programOptions}
          />
        )}
        {dataType === 'dualScatterplots' && (
          <DualScatterplotsSettingsPanel
            classes={classes}
            scatterplotOptionsData={scatterplotOptionsData}
            onSubmit={handleSubmitScatterplotSettings}
          />
        )}
        {dataType === 'searchVariants' && (
          <SearchVariantsSettingsPanel
            classes={classes}
            onSearch={handleVariantSearch}
            onClearResults={handleClearVariantSearch}
          />
        )}
        {dataType === 'searchGenotypes' && (
          <SearchGenotypesSettingsPanel
            classes={classes}
            onSearchVariants={handleGenotypeVariantSearch}
            onClearResults={handleClearGenotypeSearch}
            variantOptions={genotypeVariantResults}
            variantsToSearch={variantsToSearch}
            onVariantsToSearchChange={setVariantsToSearch}
            onSearchGenotypes={handleSearchGenotypes}
          />
        )}
        {/* Variant search results table */}
        {submitted && dataType === 'searchVariants' && (
          <SearchVariantsResultsTable data={variantSearchResults} breadCrumbs={breadCrumbs} />
        )}
        {/* Genotype search: variant results with Add Variant, inside Variants/Genotypes tabs */}
        {genotypeSubmitted && dataType === 'searchGenotypes' && (
          <SearchGenotypesResults
            data={genotypeVariantResults}
            breadCrumbs={breadCrumbs}
            onAddVariant={handleAddVariantToSearch}
            onRemoveVariant={handleRemoveVariantFromSearch}
            addedVariantNames={variantsToSearch.map((v) => v.variantName)}
            genotypeData={genotypePatientResults}
            genotypeTabEnabled={genotypeTabEnabled}
            genotypeSearchTrigger={genotypeSearchTrigger}
            genotypeVariantSearchTrigger={genotypeVariantSearchTrigger}
          />
        )}
        {/* Compare data table and boxplot modal */}
        {submitted && dataType === 'compareAnalytePrograms' && tableData.length > 0 && (
          <>
            <Grid container alignItems="center" spacing={2} sx={{ my: 2 }}>
              <Grid item xs={0} sm={3}>
                {/* Left spacer */}
              </Grid>

              <Grid item xs={12} sm={6} textAlign="center">
                <h2 style={{ margin: 0 }}>
                  Analyte Summary by Program - {tableData[0]?.description} ({tableData[0]?.analyte_code})
                </h2>
              </Grid>

              <Grid item xs={12} sm={3} textAlign="right">
                <Button 
                  variant="outlined" 
                  onClick={() =>
                    exportToCSV(
                      csvData,
                      `ED3N_analyte_summary_by_program_${sanitizeForFilename(tableData[0]?.description)}_${sanitizeForFilename(tableData[0]?.analyte_code)}.csv`,
                      csvHeaders,
                      ['mean', 'median', 'stddev', 'min', 'q1', 'q3', 'max', 'n'] // Numeric fields to preserve trailing/leading zeros in Excel
                    )
                  } 
                  startIcon={<FileDownload />}
                >
                  Export
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => setShowModal(true)} 
                  sx={{ ml: 1 }} 
                  startIcon={<CandlestickChart />}
                  disabled={boxPlotChartData.length === 0}
                >
                  View Boxplot
                </Button>
              </Grid>
            </Grid>
            <CompareAnalyteDataTable data={tableData} />
            {boxPlotChartData.length > 0 && (
              <BoxplotModal
                open={showModal}
                onClose={() => setShowModal(false)}
                data={boxPlotChartData}
                title={`Analyte Summary by Program - ${tableData[0]?.description} (${tableData[0]?.analyte_code})`}
                xAxisLabel="Program Name"
                yAxisLabel="Analyte Value (μmol/L)"
                xAxisKey="program_id"
                showZScore={false}
                allStatistics={statistics}
                selectedAnalyte={compareAnalyteSettings.selectAnalyte?.analyteCode}
                programOptions={programOptions}
                userProgramId={programId}
                formatProgramId={formatProgramId}
                roundNumericFields={roundNumericFields}
                onFetchStatistics={handleFetchPlotOptionsData}
                initialDataScope={null}
                minWidthPercent={40}
                constrainChartWidth
              />
            )}
          </>
        )}
        {/* Scatterplot chart */}
        {submitted && dataType === 'dualScatterplots' && showScatterplotChart && (
          <Box
            sx={{
              outline: '1px solid var(--alt-black)',
              borderRadius: 2,
              mt: 4,
            }}
          >
            <Grid container alignItems="center" spacing={2} sx={{ my: 2 }}>
              <Grid item xs={0} sm={2}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setScatterplotPlotOptionsOpen(true)}
                  sx={{ ml: 2 }}
                  startIcon={<Settings />}
                >
                  Options
                </Button>
              </Grid>
              <Grid item xs={12} sm={8} textAlign="center">
                <h2 style={{ margin: 0 }}>
                  {scatterplotData?.plotProperties?.plotTitle || 'Scatterplot Chart'}
                  {scatterplotDataScope === 'my_program' && ` for Program ${programId}`}
                  {scatterplotDataScope === 'all_programs' && ' for All Programs'}
                </h2>
                {scatterplotData?.plotProperties?.plotSubtitle && (
                  <div style={{ fontSize: '1rem', color: '#666', marginTop: 4 }}>
                    {scatterplotData.plotProperties.plotSubtitle}
                  </div>
                )}
              </Grid>
              <Grid item xs={12} sm={2} textAlign="right">
                {!canExportScatterplot && (
                  <Tooltip title="No data to export" placement="top" arrow>
                    <InfoOutlined
                      tabIndex={0}
                      role="img"
                      aria-label="No data to export"
                      sx={{ fontSize: '1.1rem', color: 'var(--info-color)', cursor: 'default', verticalAlign: 'middle', mr: 1 }}
                    />
                  </Tooltip>
                )}
                <Button
                  sx={{ mr: 3 }}
                  variant="outlined"
                  startIcon={<FileDownload />}
                  onClick={() => scatterplotRef.current?.exportAsPNG()}
                  disabled={!canExportScatterplot}
                  aria-label="Export chart as PNG"
                >
                  Export
                </Button>
              </Grid>
            </Grid>
            <Box sx={{ padding: '20px'}}>
              <ScatterplotChart
                ref={scatterplotRef}
                data={scatterplotData}
                showDensityCloud={showScatterplotDensityCloud}
              />
            </Box>
          </Box>
        )}
        {/* Default spacer to push the footer down until result content is displayed */}
        {!submitted && <Box sx={{ minHeight: { xs: 200, md: 320 } }} />}
      </Box>

      {/* Plot Options Dialog for Scatterplot */}
      <PlotOptionsDialog
        open={scatterplotPlotOptionsOpen}
        onClose={() => setScatterplotPlotOptionsOpen(false)}
        onSave={handleScatterplotPlotOptionsSave}
        currentDataScope={scatterplotDataScope}
        showCutoffLinesToggle={true}
        hasCutoffData={false}
        showRegressionLineToggle={true}
        hasRegressionData={false}
        showDensityCloudToggle={true}
        currentShowDensityCloud={showScatterplotDensityCloud}
        hasDensityData={hasScatterplotDensityData}
        densityDisabledMessage={scatterplotDensityDisabledMessage}
      />
    </>
  );
};

export default Explore;
